/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import { IDataPollProps, IEmailSelectedGroup, IGeoRegion, IPassword, IProfile, ISmtpEmail, ISnmpAgent, ISnmpGroup, IUser,IThresholdCrossingSettingProfile,IThresholdCrossingSettingAction,IThresholdCrossingSettingSchedule,IThresholdCrossingSettingRule } from "../store/api/admin/types";
import { IAllDevices, ISiteGroup } from "../store/api/devices/types";
import { IList } from "../store/api/tasks/types";
import { DATEFORMATS, OP_CODE_GROUPS } from "../globals/globals";
import { xsdPathStr } from "../globals/constants";
import dayjs from "dayjs";
import { setIsCreateEmailGroup } from "../store/api/admin/reducers";
import { devNull } from "os";

export const userPayload = (userFormData: IUser, passNeverExpire: boolean) => ({
  id: userFormData.id,
  username: userFormData.username,
  password: userFormData.password,
  profile: Object.assign({}, { id: Number(userFormData.profileId) }),
  wsProfile: null,
  passExpireDate: passNeverExpire ? 0 : new Date(userFormData.passExpireDate).getTime(),
  passExpireInterval: passNeverExpire ? 0 : userFormData.passExpireInterval,
  //userExpired: userFormData?.userExpired,
  hostname: userFormData.hostname,
  firstName: userFormData.firstName,
  lastName: userFormData.lastName,
  department: userFormData.department,
  location: userFormData.location,
  phone: userFormData.phone,
  email: userFormData.email,
  banned: userFormData.banned,
  firstTime: !!userFormData.id ? false : true,
  clearPasswordHistory: userFormData.clearPasswordHistory,
  history: userFormData.history,
});

export const changePasswordPayload = (userFormData: IPassword) => ({
  id: userFormData.id,
  username: userFormData.username,
  password: userFormData.password,
  newPassword: userFormData.newPassword,
});

const handleAgentVersion = (enableV2: boolean, enableV3: boolean, agentEnable: boolean) => {
  if (!agentEnable) return "NONE";
  if (enableV2 && enableV3) {
    return "BOTH";
  } else if (enableV2) {
    return "V2";
  } else if (enableV3) {
    return "V3";
  } else return "NONE";
};

export const snmpAgentPayload = (
  agentFormData: ISnmpAgent,
  agentEnable: boolean,
  enableV2: boolean,
  enableV3: boolean,
  authPassword: string,
  privPassword: string,
  isAuthChecked: boolean,
  isPrivChecked: boolean
) => {
  let payload: any = {};
  payload[`snmpAgentEnable`] = agentEnable;
  payload[`agentVersion`] = handleAgentVersion(enableV2, enableV3, agentEnable);
  payload[`port`] = Number(agentFormData?.port);
  payload[`enableV2`] = agentEnable !== true ? false : enableV2;
  payload[`readComunity`] = agentFormData?.readComunity;
  payload[`writeComunity`] = agentFormData?.writeComunity;
  payload[`enableV3`] = agentEnable !== true ? false : enableV3;
  payload[`engineId`] = agentFormData?.engineId;
  payload[`userName`] = agentFormData?.userName;
  payload[`authProtocol`] = agentEnable !== true ? "NO_AUTH" : agentFormData?.authProtocol;
  payload[`privProtocol`] = agentEnable !== true ? "NO_PRIV" : agentFormData?.privProtocol;
  
  // If checkbox is checked, use new password; otherwise use existing password from backend
  payload[`authPassword`] = agentFormData?.authProtocol?.toLowerCase() === "none" 
    ? "" 
    : (isAuthChecked ? authPassword : agentFormData?.authPassword || "");
  payload[`privPassword`] = agentFormData?.privProtocol?.toLowerCase() === "none" 
    ? "" 
    : (isPrivChecked ? privPassword : agentFormData?.privPassword || "");
  return payload;
};

const handleTrapVersion = (enableV2: boolean, enableV3: boolean) => {
  if (enableV2 && enableV3) {
    return "both";
  } else if (enableV2) {
    return "v2";
  } else if (enableV3) {
    return "v3";
  } else return "v2";
};

export const prepareTrapReceivePayload = (
  trapReceiveData: any,
  snmpv1v2: boolean,
  snmpv3: boolean,
  isAuthProtoChecked: boolean,
  isPrivProtoChecked: boolean,
  authPassword: string,
  privPassword: string,
  trapAuthPassword: string,
  trapPrivPassword: string
) => {
  const { trapReceive } = xsdPathStr;
  let payload: any = {};

  payload[`${trapReceive["authPassword"]}`] = trapReceiveData?.authProtocol?.toLowerCase() === "none" ? "" : authPassword;
  payload[`${trapReceive["authProtocol"]}`] = trapReceiveData?.authProtocol;
  payload[`${trapReceive["community"]}`] = trapReceiveData?.community;
  payload[`${trapReceive["engineId"]}`] = trapReceiveData?.engineId;
  payload[`${trapReceive["port"]}`] = trapReceiveData?.port || 162;
  payload[`${trapReceive["privPassword"]}`] = trapReceiveData?.authProtocol?.toLowerCase() === "none" ? "" : privPassword;
  payload[`${trapReceive["privProtocol"]}`] = trapReceiveData?.privProtocol;
  payload[`${trapReceive["userName"]}`] = trapReceiveData?.userName;
  payload[`${trapReceive["version"]}`] = handleTrapVersion(snmpv1v2, snmpv3);
  payload[`${trapReceive["recievePort"]}`] = trapReceiveData?.recievePort || "8162";

  if (isAuthProtoChecked && trapAuthPassword) {
    payload[`${trapReceive["trapAuthPassword"]}`] = trapAuthPassword;
  }
  if (isPrivProtoChecked && trapPrivPassword) {
    payload[`${trapReceive["trapPrivPassword"]}`] = trapPrivPassword;
  }

  return payload;
};

export const snmpGroupPayload = (FormData: ISnmpGroup, snmpVersion: string) => ({
  objectKey: FormData?.name || "",
  parentKey: "NONE",
  name: FormData?.name || "",
  port: Number(FormData?.port) || 0,
  ipAddresses: FormData?.ipAddressesList?.split(",") || [],
  enabled: FormData?.enabled,
  community: snmpVersion === "V2C" ? FormData?.community || "public" : "",
  version: snmpVersion === "V2C" ? 2 : 3,
  contextName: snmpVersion === "V3" ? FormData?.contextName || "" : "",
  userName: snmpVersion === "V3" ? FormData?.userName || "" : "",
  authPassword: snmpVersion === "V3" ? FormData?.authPassword || "" : "",
  privPassword: snmpVersion === "V3" ? FormData?.privPassword || "" : "",
  authProtocol: snmpVersion === "V3" ? FormData?.authProtocol || "NO_AUTH" : "NO_AUTH",
  privProtocol: snmpVersion === "V3" ? FormData?.privProtocol || "NO_PRIV" : "NO_PRIV",
  enableHeartbeat: FormData?.enableHeartbeat,
  heartbeatInterval: Number(FormData?.heartbeatInterval) || 0,
  // lastHeartbeatTrap: new Date().getMilliseconds(),
});

export const emailSmtpPayload = (formData: ISmtpEmail, sslVal: string, testEmailVal: any) => {
  let payload: any = {};
  payload[`useSmtpAuth`] = formData?.useSmtpAuth;
  payload[`smtpHost`] = formData?.smtpHost || "";
  payload[`smtpPort`] = Number(formData?.smtpPort) || 25;
  payload[`smtpUsername`] = formData?.smtpUsername || "";
  payload[`smtpPassword`] = formData?.smtpPassword || "";
  payload[`emailKey`] = formData?.emailKey || "";
  payload[`useSmtpSsl`] = sslVal === "ssl" ? true : false;
  payload[`fromEmail`] = formData?.fromEmail || "";
  payload[`useSmtpTls`] = sslVal === "startls" ? true : false;
  if (formData?.testEmail !== testEmailVal) {
    payload[`testEmail`] = formData?.testEmail || "";
  }
  return payload;
};

export const dataPollerPayload = (formData: IDataPollProps) => {
  let payload: any = {};
  const { dataPollingProperties } = xsdPathStr;
    payload[`${dataPollingProperties?.privatekey}`] = formData?.privatekey;
    payload[`${dataPollingProperties?.port}`] = formData?.port || "";
    payload[`${dataPollingProperties?.dbgEnable}`] = formData?.dbgEnable;
    payload[`${dataPollingProperties?.kpiEnable}`] = formData?.kpiEnable;
    payload[`${dataPollingProperties?.alarmEnable}`] = formData?.alarmEnable;
    payload[`${dataPollingProperties?.enable}`] = formData?.enable;
    payload[`${dataPollingProperties?.cmEnable}`] = formData?.cmEnable;
    payload[`${dataPollingProperties?.ebEnable}`] = formData?.ebEnable;
    payload[`${dataPollingProperties?.maxDays}`] = formData?.maxDays;
    payload[`${dataPollingProperties?.pubkey}`] = formData?.pubkey;
    payload[`${dataPollingProperties?.host}`] = formData?.host;
  return payload;
};

export const emailGroupPayload = (FormData: IEmailSelectedGroup) => ({
  parentKey: FormData?.parentKey || "",
  domainKey: "Initial Domain",
  objectKey: FormData?.parentKey || "",
  email: FormData?.email || "",
  eventTypes: null,
});

export const resetUser = {
  id: null,
  username: "",
  password: "",
  confirmPassword: "",
  passExpireDate: 0,
  passExpireInterval: null,
  hostname: "",
  firstName: "",
  lastName: "",
  department: "",
  location: "",
  phone: "",
  email: "",
  banned: false,
  firstTime: false,
  history: "",
  clearPasswordHistory: false,
  externalAuth: false,
  loginAuthSource: "",
  key: "",
  parentKey: "",
  userExpired: false,
  dbConnType: "",
  profileId: 0,
  profile: {
    id: null,
    name: "",
    taskTypes: {},
    managementDomains: [],
    opCodeGroups: [],
    domainOpCodeGroups: {},
    adminOperations: {},
    applicationOperations: {},
    geoRegions: [],
  },
};

export const resetSnmpGroup = {
  key: "",
  parentKey: "",
  name: "",
  port: 162,
  ipAddressesList: "",
  enabled: true,
  community: "public",
  version: 1,
  contextName: "",
  userName: "",
  authPassword: "",
  authConfirmPassword: "",
  privPassword: "",
  privConfirmPassword: "",
  authProtocol: "",
  privProtocol: "",
  enableHeartbeat: false,
  heartbeatInterval: 5,
  lastHeartbeatTrap: "",
};

export const resetSnmpAgent = {
  snmpAgentEnable: false,
  agentVersion: "",
  port: "",
  enableV2: true,
  readCommunity: "",
  writeCommunity: "",
  enableV3: false,
  engineId: "",
  userName: "",
  authProtocol: "",
  privProtocol: "",
  authPassword: "",
  privacyPassword: "",
};

export const resetSmtpServer = {
  useSmtpAuth: false,
  smtpHost: "",
  smtpPort: "25",
  smtpUsername: "",
  smtpPassword: "",
  emailKey: "",
  useSmtpSsl: false,
  fromEmail: "",
  useSmtpTls: false,
  testEmail: "",
};

export const resetDataPoller = {
  privatekey: "",
  port: 22,
  dbgEnable: false,
  kpiEnable: false,
  alarmEnable: false,
  enable: false,
  cmEnable: false,
  ebEnable: false,
  maxDays: 3,
  pubkey: "",
  host: ""
};

export const resetEmailGroup = {
  email: "",
  eventTypes: "",
  parentKey: "",
  objectKey: "",
  domainKey: "Initial Domain",
};

export const resetSelectedTemplate = {
  id: null,
  domainKey: "",
  imageConstraints: "",
  nodeTypeKey: "",
  templateName: "",
  deviceKey: "",
};

export const initialGeoRegion: IGeoRegion = {
  objectKey: "",
  name: "",
};

export const initialProfile: IThresholdCrossingSettingProfile = {
  id: 0,
  name: "",
  description:"",
  enabled: true,
  actions:[],
  schedules:[],
  rules:[],
  devices :[]
};

export const resetProfile: IProfile = {
  id: 0,
  name: "",
  geoRegions: [],
  managementDomains: [],
  taskTypes: { "": ["all"] },
  opCodeGroups: ["view_topology", "view_profile"],
  domainOpCodeGroups: {},

  adminOperations: undefined,
  applicationOperations: undefined,
};

export function getAdminOperations(domainList: { [domain: string]: string[] }, domain: string): string[] {
  let response: string[] = [];

  if (domainList == null || Object.keys(domainList) == null) {
    return response;
  }
  const adminOpCodeGroups = Object.keys(OP_CODE_GROUPS).filter((key) => !!OP_CODE_GROUPS[key].isAdmin);
  const keys = Object.keys(domainList);

  if ([null, "", "*", "Initial Domain"].includes(domain)) {
    if (!keys.some((v) => ["", "*", "Initial Domain"].includes(v))) {
      return response;
    }
    ["", "*", "Initial Domain"].forEach((domainKey) => {
      if (!keys.includes(domainKey)) return;

      domainList[domainKey].forEach((opCode) => {
        if (adminOpCodeGroups.includes(opCode) && !response.includes(opCode)) {
          response.push(opCode);
        }
      });
    });

    return response;
  }

  if (!keys.includes(domain)) {
    return response;
  }

  domainList[domain].forEach((opCode) => {
    if (adminOpCodeGroups.includes(opCode) && !response.includes(opCode)) {
      response.push(opCode);
    }
  });

  return response;
}

export function getAppOperations(domainList: { [domain: string]: string[] }, domain: string): string[] {
  let response: string[] = [];

  if (domainList == null || Object.keys(domainList) == null) {
    return response;
  }

  const appOpCodeGroups = Object.keys(OP_CODE_GROUPS).filter((key) => !OP_CODE_GROUPS[key].isAdmin);
  const keys = Object.keys(domainList);

  if (["", "*", "Initial Domain"].includes(domain)) {
    if (!keys.some((v) => ["", "*", "Initial Domain"].includes(v))) {
      return response;
    }
    ["", "*", "Initial Domain"].forEach((domainKey) => {
      if (!keys.includes(domainKey)) return;

      domainList[domainKey].forEach((opCode) => {
        if (appOpCodeGroups.includes(opCode) && !response.includes(opCode)) {
          response.push(opCode);
        }
      });
    });

    return response;
  }

  if (!keys.includes(domain)) {
    return response;
  }

  domainList[domain].forEach((opCode) => {
    if (appOpCodeGroups.includes(opCode) && !response.includes(opCode)) {
      response.push(opCode);
    }
  });

  return response;
}

export const menuProps = {
  PaperProps: {
    sx: {
      backgroundColor: "var(--card)",
      color: "var(--foreground)",
      "& .MuiMenuItem-root": {
        color: "var(--foreground)",
        "&:hover": {
          backgroundColor: "var(--accent)",
        },
        "&.Mui-selected": {
          backgroundColor: "var(--accent)",
          "&:hover": {
            backgroundColor: "var(--accent)",
          },
        },
      },
    },
  },
  disableScrollLock: true,
};

export const menuSlotProps = {
  paper: {
    disableScrollLock: true,
    elevation: 0,
    sx: {
      overflow: "visible",
      filter: "drop-shadow(1px 1px 2px var(--muted-foreground))",
      mt: 0.5,
      // "& .MuiSvgIcon-root": {
      //   width: 30,
      //   height: 30,
      //   ml: -0.5,
      //   mr: 2,
      // },
      "& .MuiAvatar-root": {
        width: 30,
        height: 30,
        ml: -0.5,
        mr: 2,
      },
      "&:before": {
        content: '""',
        display: "block",
        position: "absolute",
        top: 0,
        right: 14,
        width: 10,
        height: 10,
        bgcolor: "var(--card)",
        transform: "translateY(-50%) rotate(45deg)",
        zIndex: 0,
      },
      backgroundColor: "var(--card)",
      color: "var(--foreground)",
      "& .MuiMenuItem-root": {
        color: "var(--foreground)",
        "&:hover": {
          backgroundColor: "var(--accent)",
        },
      },
    },
  },
};

export const moreIconButtonSxProps: SxProps = {
  color: "var(--foreground)",
  "&:hover": { backgroundColor: "var(--secondary)" },
};

export const notificationSlotProps = {
  paper: {
    sx: {
      backgroundColor: "var(--card)",
      width: "592px",
      maxHeight: "539px",
      top: { md: "50px !important" },
      left: { md: "auto !important" },
      right: { md: "16px" },
    },
  },
};

export const listPopoverProps: SxProps = {
  maxHeight: "437px",
};

export const headerStyle: SxProps = {
  position: "sticky",
  top: "0px",
  width: "100%",
  zIndex: 100,
  pl: 4,
  height: "50px",
  alignItems: "center",
  display: "flex",
};
export const footerStyle: SxProps = {
  position: "sticky",
  bottom: "0px",
  width: "100%",
  zIndex: 100,
  pl: 4,
  height: "50px",
  alignItems: "center",
  display: "flex",
  justifyContent: "space-around",
};
export const footerDividerStyle: SxProps = {
  position: "sticky",
  bottom: "0px",
  width: "100%",
  zIndex: 100,
  alignItems: "center",
};

export const getAllDeviceList = (deviceData: IAllDevices[]) => {
  let deviceList: IList[] = [];
  if (deviceData.length > 0) {
    deviceList = deviceData.map((item: IAllDevices) => ({ title: item.name || item.ipAddress, value: item.objectkey,deviceConfiguration: item.deviceConfiguration }));
  }
  return deviceList;
};

export const getAllSiteGroupList = (siteGroupData: ISiteGroup[]) => {
  let deviceList: IList[] = [];
  if (siteGroupData.length > 0) {
    deviceList = siteGroupData.map((item: ISiteGroup) => ({ title: item.name || "", value: item.objectKey || "" }));
  }
  return deviceList;
};

export const iconButtonSxProps: SxProps = {
  cursor: "pointer",
  color: "var(--muted-foreground)",
  //backgroundColor: "var(--secondary)",
  "&:hover": { backgroundColor: "var(--border)" },
};

export const toDate = (value: any, format: string | undefined) => {
  return value <= 0
    ? "0000-00-00 00:00:00"
    : dayjs(value)
      .format(format ? format : DATEFORMATS.TS)
      .replace(/:(?=[0-9]{2}$)/, "");
};

export const resetSiteOfflineDetails = {
  group: "",
  siteType: "",
  siteName: "",
  serialNumber: "",
  siteLabel: "",
  market: "",
  version: "",
  eNBId: "",
  state: "",
  latitude: "",
  longitude: "",
  modelNumber: "",
  description: "",
  ipAddress: "",
  snmpVersion: "1",
  snmpPort: 161,
  timeout: 5,
  writeCommunity: "",
  readCommunity: "",
  username: "",
  contextName: "",
  authProtocol: "",
  authPassword: "",
  privacyProtocol: "",
  privacyPassword: "",
}

export const resetThresholdCrossingSettingProfile = {
  id: 0,
  name: "",
  description:"",
  enabled:false,
  actions:[],
  schedules:[],
  rules:[],
  devices:[]
};


export const resetThresholdCrossingSettingAction = {
    id: 0,
    actionType: "",
    details: [],
    performanceProfileId:null
};


export const resetThresholdCrossingSettingSchedule = {
  id: 0,
  days:[],
  fromTime:dayjs().format("hh:mm A"),
  toTime:dayjs().add(15, "minute").format("hh:mm A"),
  performanceProfileId:null
};

export const resetThresholdCrossingSettingRule = {
  id: 0,
  profileId:"",
  kpiType:"", 
  kpi:"", 
  operator : null,
  thresholdAbove:null,
  thresholdBelow:null,
  consecutiveSamples:1,
  performanceProfileId:null
};

export const thresholdCrossingSettingProfilePayload = (userFormData: IThresholdCrossingSettingProfile) => ({
  id: userFormData.id,
  name: userFormData.name.trim(),
  description:userFormData.description.trim(),
  enabled:userFormData.enabled,
});

export const thresholdCrossingSettingActionPayload = (userFormData: IThresholdCrossingSettingAction) => ({
  id: userFormData.id,
  actionType: userFormData.actionType,
  details:userFormData.details,
  performanceProfileId : userFormData.performanceProfileId
});


export const thresholdCrossingSettingSchedulePayload = (userFormData: IThresholdCrossingSettingSchedule) => ({
  id: userFormData.id,
  days: userFormData.days,
  fromTime:userFormData.fromTime,
  toTime:userFormData.toTime,
  performanceProfileId : userFormData.performanceProfileId
});


export const thresholdCrossingSettingRulePayload = (userFormData: IThresholdCrossingSettingRule) => ({
  id: userFormData.id,
  kpi: userFormData.kpi,
  kpiType: userFormData.kpiType,
  thresholdAbove: userFormData.thresholdAbove,
  thresholdBelow: userFormData.thresholdBelow,
  consecutiveSamples: userFormData.consecutiveSamples,
  performanceProfileId : userFormData.performanceProfileId
});

export interface IDeviceStatusIndicatorInput {
  isOffline: boolean;
  statusCode?: number;
  operationMode?: string;
}

export interface IDeviceStatusIndicatorStyles {
  baseWrapperClassName?: string;
  baseIconClassName?: string;
  offlineTextClassName?: string;
  disconnectedTextClassName?: string;
  maintenanceTextClassName?: string;
  connectedTextClassName?: string;
}

export interface IDeviceStatusIndicatorConfig {
  label: string;
  iconName: string;
  wrapperClassName: string;
  iconClassName: string;
}

// Device status code used by backend to indicate a disconnected/timeout link state.
const DEVICE_STATUS_DISCONNECTED = 19;

const mergeClasses = (...classes: Array<string | undefined>) => classes.filter(Boolean).join(" ");

export const getDeviceStatusIndicatorConfig = (
  { isOffline, statusCode, operationMode }: IDeviceStatusIndicatorInput,
  styles?: IDeviceStatusIndicatorStyles
): IDeviceStatusIndicatorConfig => {
  const baseWrapperClassName = styles?.baseWrapperClassName || "text-sm inline-flex items-center gap-2 min-w-0";
  const baseIconClassName = styles?.baseIconClassName || "shrink-0";

  if (isOffline) {
    return {
      label: "Offline",
      iconName: "link_off",
      wrapperClassName: mergeClasses(baseWrapperClassName, styles?.offlineTextClassName),
      iconClassName: mergeClasses("text-muted-foreground", baseIconClassName),
    };
  }

  if (statusCode === DEVICE_STATUS_DISCONNECTED) {
    return {
      label: "Disconnected",
      iconName: "link_off",
      wrapperClassName: mergeClasses(baseWrapperClassName, styles?.disconnectedTextClassName || "text-destructive"),
      iconClassName: mergeClasses("text-destructive", baseIconClassName),
    };
  }

  if (operationMode === "Maintenance") {
    return {
      label: "In Maintenance",
      iconName: "build",
      wrapperClassName: mergeClasses(baseWrapperClassName, styles?.maintenanceTextClassName || "text-warning"),
      iconClassName: mergeClasses("text-warning", baseIconClassName),
    };
  }

  return {
    label: "Connected",
    iconName: "link",
    wrapperClassName: mergeClasses(baseWrapperClassName, styles?.connectedTextClassName),
    iconClassName: mergeClasses("text-success", baseIconClassName),
  };
};
